import { isDefinedString, reduce } from "et3-type-guards";
import { memoize } from "lodash";
import { computed, observable, runInAction } from "mobx";
import { apiCallAddUserToInvestigation } from "../../api/user-api/api-call-add-user-to-investigation";
import { TEMPORARY_BAN_DEFAULT_DAYS_NUMBER } from "../../constants/input-entries-constants";
import {
  StringOrNumber,
  TBWTypes,
  TCorePage,
  TTBWExtraData,
  TTBWReason,
  TTBWReasonType
} from "../../Types";
import getCorePersonalStore from "../core/core-personal-store";
import getCorePopUpStore from "../core/core-popup-store";
import getUserAdvancedStore from "../user-store/advanced-store";
import getUserStore from "../user-store/index";
import { banFromTBW } from "./ban-from-tbw";
import getTBWReasonsFromDatabase from "./get-tbw-reasons";
import getApproveRejectStore from "../approval-reject-store";
import { TEt3PickerMultipleValue, TEt3PickerSingleValue } from "../../TypesNEW";

class TBWStore {
  isTBWPopUpVisible = observable.box<boolean>(false);
  TBWExtraData = observable.box<TTBWExtraData>({
    userID: -1,
    userUUID: "",
    reportID: -1,
    reportedUUID: "",
    reportedID: -1,
    secondPartyID: -1
  });
  TBWSelectedReason = observable.box<TEt3PickerMultipleValue>([]);
  TBWFromCase = observable.box<TBWTypes>("");
  TBWAuditIssue = observable.box<"minor" | "major" | "">("");

  TBWPopUpType = observable.box<string>("");
  TBWreasonType = observable.box<TEt3PickerSingleValue>({
    label: "Choose",
    value: ""
  });
  TBWreasons = observable.box<TTBWReason[]>([]);
  TBWreasonsByType = observable.map({});
  TBWFromPage = observable.box<TCorePage | "">("");
  TBWHeader = observable.box<string>("");
  TBWEventId = observable.box<StringOrNumber>();
  TBWSaveButtonDisabled = observable.box<boolean>(false);
  auditEventProof = observable.box<string>("");
  superviseEventProof = observable.box<string>("");

  temporaryBanDays = observable.box<number>(TEMPORARY_BAN_DEFAULT_DAYS_NUMBER);

  saveTbwReasonsIsEnabled = computed(() => {
    const firstValue = reduce(() => this.TBWSelectedReason.get()[0].label, "");
    return isDefinedString(firstValue);
  });

  resetTBWPopUp = () => {
    this.TBWHeader.set("");
    this.TBWreasonsByType.clear();
    this.TBWreasons.set([]);
    this.TBWPopUpType.set("");
    this.TBWreasonType.set({
      label: "Choose",
      value: ""
    });
    this.TBWFromCase.set("");
    this.TBWAuditIssue.set("");
    this.TBWSaveButtonDisabled.set(false);
    this.isTBWPopUpVisible.set(false);
    this.TBWExtraData.set({
      userID: -1,
      userUUID: "",
      reportID: -1,
      reportedID: -1,
      reportedUUID: "",
      secondPartyID: -1
    });
    this.TBWSelectedReason.set([]);
    this.auditEventProof.set("");
    this.superviseEventProof.set("");
  };
  hideTBWPopUp = () => {
    this.isTBWPopUpVisible.set(false);
  };
  getTBWReasons = async () => {
    const status = await getTBWReasonsFromDatabase();
    return status;
  };
  setTBWreasonType = (value: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.TBWreasonType.set(value);
    });
  };

  getTBWExtraData = () => {
    const TBW_ids = this.getTWBValuesArray();
    const newStatus = this.TBWFromCase.get();
    const temporaryBanDays =
      newStatus === "temporary_banned" ? this.temporaryBanDays.get() : 0;
    return { TBW_ids, temporaryBanDays };
  };

  showActionTBWPopUp = async ({
    type,
    headerMessage,
    fromPage,
    actionName,
    extraData,
    eventId,
    issueAudit
  }: {
    type: string;
    headerMessage: string;
    fromPage: TCorePage | "";
    actionName: TBWTypes;
    extraData: TTBWExtraData;
    eventId: StringOrNumber;
    issueAudit: "major" | "minor" | "";
  }) => {
    const reason: TTBWReasonType =
      actionName === "investigate"
        ? "INVESTIGATE_REASON"
        : issueAudit === "major" || issueAudit === "minor"
          ? "AUDIT_REASON"
          : (actionName.toUpperCase() as TTBWReasonType);
    getTBWStore().setTBWreasonType({
      label: reason,
      value: reason
    });
    const status = await getTBWStore().getTBWReasons();

    this.TBWFromCase.set(actionName);
    this.TBWAuditIssue.set(issueAudit);
    this.TBWExtraData.set(extraData);
    this.isTBWPopUpVisible.set(true);
    this.TBWPopUpType.set(type);
    this.TBWFromPage.set(fromPage);
    this.TBWHeader.set(headerMessage);
    this.TBWEventId.set(eventId);
    return status;
  };

  setTBWActionDataShortcuts = ({
    actionName,
    extraData,
    reason
  }: {
    actionName: TBWTypes;
    extraData: TTBWExtraData;
    reason: TEt3PickerMultipleValue;
  }) => {
    const fromPage: TCorePage = "REPORTS";

    runInAction(() => {
      this.TBWFromCase.set(actionName);
      this.TBWExtraData.set(extraData);
      this.TBWFromPage.set(fromPage);
      this.TBWSelectedReason.set(reason);
    });
  };

  resetTBWActionDataShortcut = () => {
    runInAction(() => {
      this.TBWFromCase.set("");
      this.TBWExtraData.set({
        userID: -1,
        userUUID: "",
        reportID: -1,
        reportedID: -1,
        reportedUUID: "",
        secondPartyID: -1
      });
      this.TBWSelectedReason.set([]);
    });
  };

  warnBanUser = async ({
    shouldAddToInvestigate
  }: {
    shouldAddToInvestigate: boolean;
  }) => {
    this.hideTBWPopUp();

    if (this.TBWFromCase.get() === "warning") {
      const status = await getUserStore().warnUser();
      if (status === "ERROR") {
        if (!getCorePopUpStore().isAlertVisible.get()) {
          getCorePopUpStore().showAlertMessage("ERROR");
        }
        return;
      }
    } else {
      const status = await banFromTBW();
      if (status === "ERROR") {
        return;
      }
    }
    const fromPage = this.TBWFromPage.get();

    if (!shouldAddToInvestigate) {
      if (this.TBWFromCase.get() === "warning") {
        getCorePopUpStore().showSuccessMessage("USER_WARNED_SUCCESSFULLY");
      } else {
        const userUUID = reduce(() => this.TBWExtraData.get().userUUID, "");
        if (fromPage === "APPROVAL_DATA") {
          getApproveRejectStore()
            .approvalData.get()
            .forEach((value, index) => {
              if (value.userUUID === userUUID) {
                getApproveRejectStore().approvalData.get().splice(index, 1);
                getApproveRejectStore().decreaseDashboardApprovalCount();
              }
            });
        }
        getCorePopUpStore().showSuccessMessage("USER_BANNED_SUCCESSFULLY");
      }
    }
    if (shouldAddToInvestigate) {
      this.investigateUser(fromPage);
    }
  };

  investigateUser = async (fromPage: TCorePage | "") => {
    const reportedUUID = reduce(() => this.TBWExtraData.get().reportedUUID, "");
    const reportID = reduce(() => this.TBWExtraData.get().reportID, -1);
    const secondPartyID = reduce(
      () => this.TBWExtraData.get().secondPartyID,
      0
    );
    const userID = reduce(() => this.TBWExtraData.get().userID, -1);
    const userUUID = reduce(() => this.TBWExtraData.get().userUUID, "");
    const adminId = getCorePersonalStore().user.get().id;
    const actionID =
      fromPage === "REPORTS"
        ? reportID
        : fromPage === "USER_CONVERSATIONS"
          ? secondPartyID
          : userID;
    const TBW_ids = this.getTWBValuesArray();
    const flaggedUserUUID = fromPage === "REPORTS" ? reportedUUID : userUUID;
    const { status, code } = await apiCallAddUserToInvestigation({
      adminId,
      userUUID: flaggedUserUUID,
      pageName: fromPage,
      actionName: this.TBWFromCase.get(),
      actionID,
      TBW_ids,
      temporaryBanDays: this.temporaryBanDays.get()
    });
    if (status === "OK") {
      if (fromPage === "USER_ADVANCED") {
        await getUserAdvancedStore().getUserData(false);
      }
      getCorePopUpStore().showSuccessMessage("USER_FLAGGED_SUCCESSFULLY");
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  disableTbwActionButton = computed(() => {
    const TBWSelectedReason = reduce(
      () => this.TBWSelectedReason.get()[0].label,
      ""
    );
    return !isDefinedString(TBWSelectedReason);
  });

  handleInvestigationAndWarning = async ({
    investigateOnly,
    investigateWithWarn
  }: {
    investigateOnly: boolean;
    investigateWithWarn: boolean;
  }) => {
    if (investigateOnly) {
      this.isTBWPopUpVisible.set(false);
      await this.investigateUser("USER_ADVANCED");
      return this.resetTBWPopUp();
    }
    if (investigateWithWarn) {
      await this.warnBanUser({ shouldAddToInvestigate: true });

      return this.resetTBWPopUp();
    }
  };

  setTBWReasons = (reasons: TEt3PickerMultipleValue) => {
    getTBWStore().TBWSelectedReason.set(reasons);
  };

  getTWBReasonSelectValue = () => {
    return this.TBWSelectedReason.get().filter((data) => {
      return isDefinedString(data.label);
    });
  };

  getTWBValuesArray = () => {
    return this.TBWSelectedReason.get().map((data) => {
      return parseInt(data.value, 10);
    });
  };
}

const getTBWStore = memoize(
  () => new TBWStore(),
  () => 1
);
export default getTBWStore;
